import {
  estimateSdk,
  getGetEventQueryOptions, getGetFaceQueryOptions, getGetFacesQueryOptions,
  getGetHandlersQueryOptions, getGetListsQueryOptions,
  matchBodies, matchFaces,
} from '@vlabs/api/artemy/client';
import type {
  EstimateSdkParams, GetFacesParams, MatchBodiesRequest, MatchBodiesResponse,
  MatchFacesRequest, MatchFacesResponse,
} from '@vlabs/api/artemy/model';
import type { EventRecord } from '@vlabs/api/artemy/model/EventRecord';
import type { OutputFace } from '@vlabs/api/artemy/model/OutputFace';
import { normalizeUrl } from '@vlabs/api/axios/base-client';
import { getArtemySampleUrl } from '@vlabs/api/axios/point2-client';
import { queryClient } from '@vlabs/api/queryClient';
import type {
  TCandidateOrigin, TMatchingResponse, TReference, TReferencePreview,
} from '@vlabs/monokit/entities/matcher/types/types';

import type { SearchMatchPayload } from './buildMatchPayload';
import type { SearchApi } from './SearchApi';

const ARTEMY_API_URL = import.meta.env.VITE_POINT2_API_URL;

type ArtemyReference =
  | { type: 'sdk_descriptor'; id: string; data: string }
  | { type: 'face'; id: string }
  | { type: 'event'; id: string }
  | { type: 'card_id'; id: string };

const mapArtemyReferences = (references: TReference[]): ArtemyReference[] => references.map(({
  id, type, data, face_id: faceId,
}) => {
  switch (type) {
    case 'sdk_descriptor':
      return { type: 'sdk_descriptor', id, data: data ?? '' };
    case 'event':
      return { type: 'event', id };
    case 'face_external_id':
      return { type: 'card_id', id };
    case 'face':
    default:
      return { type: 'face', id: faceId ?? id };
  }
});

const mapArtemyEventFilters = (filters: Record<string, unknown>): Record<string, unknown> => {
  const rest = { ...filters };
  delete rest.list_id;
  delete rest.attribute_ids;
  delete rest.origin;
  return { origin: 'events', ...rest };
};

const mapArtemyFaceFilters = (filters: Record<string, unknown>): Record<string, unknown> => {
  const faceFilters: Record<string, unknown> = { origin: 'faces' };

  const faceIds = filters.face_ids;
  const cardIds = filters.external_ids;
  const listId = filters.list_id;

  if (Array.isArray(faceIds) && faceIds.length > 0) faceFilters.face_ids = faceIds;
  if (Array.isArray(cardIds) && cardIds.length > 0) faceFilters.card_ids = cardIds;
  if (typeof listId === 'string' && listId.length > 0) faceFilters.list_id = listId;

  return faceFilters;
};

const mapArtemyMatchResponse = (
  response: MatchFacesResponse | MatchBodiesResponse,
): TMatchingResponse[] => response.map((item) => ({
  reference: item.reference,
  matches: item.matches.map((match) => ('error' in match
    ? { filters: match.filters, result: [] }
    : { filters: match.filters, result: match.result })),
})) as unknown as TMatchingResponse[];

const buildArtemyCandidates = (payload: SearchMatchPayload, forceOrigin?: TCandidateOrigin) => {
  const candidate = payload.candidates[0];
  if (!candidate) return [];

  const origin = (forceOrigin ?? candidate.filters.origin) as TCandidateOrigin;
  const filters = origin === 'events'
    ? mapArtemyEventFilters(candidate.filters)
    : mapArtemyFaceFilters(candidate.filters);

  return [{
    filters,
    order: candidate.order,
    limit: candidate.limit,
    targets: candidate.targets,
    threshold: candidate.threshold,
  }];
};

const getArtemyImageUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('//')) return url;
  return normalizeUrl(`${ARTEMY_API_URL}${url}`);
};

const enrichArtemyEvent = (event: EventRecord): Record<string, unknown> => {
  const faceDetections = event.face_detections?.map((detection) => ({
    ...detection,
    uiSampleURL: getArtemySampleUrl('faces', detection.sample_id),
  })) ?? null;
  const bodyDetections = event.body_detections?.map((detection) => ({
    ...detection,
    uiSampleURL: getArtemySampleUrl('bodies', detection.sample_id),
  })) ?? null;

  return {
    ...event,
    face_detections: faceDetections,
    body_detections: bodyDetections,
    uiFaceDetection: faceDetections?.find((detection) => detection.uiSampleURL) ?? null,
    uiBodyDetection: bodyDetections?.find((detection) => detection.uiSampleURL) ?? null,
  };
};

const enrichArtemyFace = (face: OutputFace): Record<string, unknown> => ({
  ...face,
  uiAvatarURL: getArtemyImageUrl(face.avatar ?? undefined),
});

const FACE_ESTIMATE_PARAMS: EstimateSdkParams = {
  detect_face: 1,
  estimate_face_descriptor: 1,
  estimate_face_warp: 1,
  image_type: 0,
};

const BODY_ESTIMATE_PARAMS: EstimateSdkParams = {
  detect_body: 1,
  estimate_body_descriptor: 1,
  estimate_body_warp: 1,
  image_type: 0,
};

const estimate = async (file: File, params: EstimateSdkParams): Promise<unknown> => {
  const data = await estimateSdk(file, params, { headers: { 'Content-Type': file.type } });
  return { data };
};

export const artemySearchApi: SearchApi = {
  matchFaces: async (payload: SearchMatchPayload) => {
    const request = {
      references: mapArtemyReferences(payload.references),
      candidates: buildArtemyCandidates(payload),
    };
    const response = await matchFaces(request as unknown as MatchFacesRequest);
    return mapArtemyMatchResponse(response);
  },
  matchBodies: async (payload: SearchMatchPayload) => {
    const request = {
      references: mapArtemyReferences(payload.references),
      candidates: buildArtemyCandidates(payload, 'events'),
    };
    const response = await matchBodies(request as unknown as MatchBodiesRequest);
    return mapArtemyMatchResponse(response);
  },
  getReferenceInfo: async (reference: TReference) => {
    const { id, face_id: faceId, type } = reference;

    if (type === 'event') {
      const event = await queryClient.fetchQuery(getGetEventQueryOptions(id));
      return [enrichArtemyEvent(event) as unknown as TReferencePreview];
    }

    if (type === 'face_external_id') {
      const params = { card_ids: [id], page_size: 100 } as unknown as GetFacesParams;
      const response = await queryClient.fetchQuery(getGetFacesQueryOptions(params));
      return (response.faces ?? []).map((face) => enrichArtemyFace(face) as unknown as TReferencePreview);
    }

    if (type === 'face') {
      const face = await queryClient.fetchQuery(getGetFaceQueryOptions(faceId ?? id));
      return [enrichArtemyFace(face) as unknown as TReferencePreview];
    }

    return undefined;
  },
  estimateFace: async (file: File) => estimate(file, FACE_ESTIMATE_PARAMS),
  estimateBody: async (file: File) => estimate(file, BODY_ESTIMATE_PARAMS),
  getHandlerOptions: async () => {
    const response = await queryClient.fetchQuery(getGetHandlersQueryOptions());
    return response.handlers.map((handler) => ({ value: handler.handler_id, label: handler.description }));
  },
  getListOptions: async () => {
    const response = await queryClient.fetchQuery(getGetListsQueryOptions());
    return (response.lists ?? []).map((list) => ({ value: list.list_id, label: list.user_data }));
  },
  capabilities: {
    referenceTypes: ['sdk_descriptor', 'face_external_id', 'face', 'event'],
    faceCandidateFilters: ['externalIdFilter', 'listsFilter', 'similarityFilter'],
    eventCandidateFilters: [],
  },
};

export {
  mapArtemyReferences, mapArtemyEventFilters, mapArtemyFaceFilters, mapArtemyMatchResponse,
};
